import { Box, Grid, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { OrgDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import { displayDateWithTime } from "../../../../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../../../../common/formatting/i18nFormatters";
import { EspdGuidingMessage } from "./EspdGuidingMessage";
import { EspdPreviewPart } from "./EspdPreviewPart";

interface Props {
  event: SourcingEventV2Dto;
  organization: OrgDto;
}

export const EspdPreviewDelI = ({ event, organization }: Props) => {
  const { t, i18n } = useTranslation();
  const { eventData, publicContext } = event;

  const procedureLabel = publicContext.procedureType ? t(publicContext.procedureType) : null;

  const deadline = eventData.bidSubmissionDeadline
    ? displayDateWithTime(eventData.bidSubmissionDeadline, t("at"), momentLocaleFor(i18n.language))
    : null;

  return (
    <EspdPreviewPart eyebrow={t("Part I")} title={t("Procurement data")} owner="autoFilled">
      <EspdGuidingMessage>
        {t("Retrieved from the notice and contracting authority. Shown as the supplier sees it.")}
      </EspdGuidingMessage>
      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} columnGap="8" rowGap="4">
        <Field label={t("Contracting authority")} value={organization.name} />
        {organization.organizationNumber && (
          <Field label={t("Org. no.")} value={organization.organizationNumber} />
        )}
        <Field label={t("Procurement title")} value={eventData.title} />
        <Field label={t("Procedure type")} value={procedureLabel} />
        <Field label={t("Bid deadline")} value={deadline} />
        <Field label={t("EU notice number")} value={null} placeholder={t("Filled in after publication") as string} />
      </Grid>
    </EspdPreviewPart>
  );
};

const Field = ({ label, value, placeholder }: { label: string; value: string | null | undefined; placeholder?: string }) => (
  <Box py="1">
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="wider" color="smMuted" mb="0.5">
      {label}
    </Text>
    {value ? (
      <Text fontSize="sm" fontWeight="medium">{value}</Text>
    ) : (
      <Text fontSize="sm" color="smMuted" fontStyle="italic">{placeholder ?? "—"}</Text>
    )}
  </Box>
);
